<template>
  <div class="admin-container">
    <!-- Sidebar -->
    <AdminSidebar :isOpen="isSidebarOpen" :userData="currentUser" />

    <!-- Main Content -->
    <div class="main-content" :class="{ 'content-shifted': isSidebarOpen }">
      <nav class="admin-nav">
        <button class="menu-btn" @click="toggleSidebar">
          <i class="fas fa-bars"></i>
        </button>
      </nav>

      <div class="admin-content">
        <div class="data-section">
          <div class="section-header">
            <h2>
              <i class="fas fa-shopping-cart"></i> Quản Lý Đơn Hàng Phụ Trợ
            </h2>
            <div class="search-filters">
              <div class="search-box">
                <i class="fas fa-search"></i>
                <input
                  type="text"
                  v-model="searchQuery"
                  placeholder="Tìm kiếm theo ID, tên khách..."
                  @keyup.enter="fetchOrders"
                />
              </div>
              <div class="filters-container">
                <div class="filter-group">
                  <label for="status-filter">Trạng thái:</label>
                  <select id="status-filter" v-model="statusFilter">
                    <option value="">Tất cả trạng thái</option>
                    <option value="pending">Chờ xử lý</option>
                    <option value="confirmed">Đã xác nhận</option>
                    <option value="shipped">Đang giao hàng</option>
                    <option value="completed">Hoàn thành</option>
                    <option value="canceled">Đã hủy</option>
                  </select>
                </div>
                <div class="filter-group">
                  <label for="date-from">Từ ngày:</label>
                  <input
                    type="date"
                    id="date-from"
                    v-model="dateFrom"
                    class="date-input"
                  />
                </div>
                <div class="filter-group">
                  <label for="date-to">Đến ngày:</label>
                  <input
                    type="date"
                    id="date-to"
                    v-model="dateTo"
                    class="date-input"
                  />
                </div>
                <button class="apply-filter-btn" @click="fetchOrders">
                  <i class="fas fa-check"></i> Áp dụng
                </button>
                <button class="reset-filter-btn" @click="resetFilters">
                  <i class="fas fa-redo"></i> Đặt lại
                </button>
              </div>
            </div>
          </div>

          <div class="table-container">
            <!-- Loading State -->
            <div v-if="isLoading" class="table-loading">
              <div class="table-spinner"></div>
              <p>Đang tải dữ liệu...</p>
            </div>

            <!-- Empty State -->
            <div v-else-if="orders.length === 0" class="table-empty">
              <i class="fas fa-shopping-cart table-empty-icon"></i>
              <h3 class="table-empty-title">Không có dữ liệu đơn hàng phụ trợ</h3>
              <p class="table-empty-text">Chưa có đơn hàng phụ trợ nào được đặt</p>
            </div>

            <!-- Table Content -->
            <div v-else class="table-responsive has-mobile-cards">
              <!-- Desktop Table -->
              <table class="admin-table">
                <thead>
                  <tr>
                    <th>Mã đơn</th>
                    <th>Booking ID</th>
                    <th>Khách hàng</th>
                    <th>Chuyến đi</th>
                    <th>Sản phẩm</th>
                    <th class="text-center">Số lượng</th>
                    <th class="text-right">Tổng tiền</th>
                    <th>Ngày đặt</th>
                    <th>Trạng thái</th>
                    <th class="col-actions">Thao tác</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="order in orders" :key="order.id">
                    <td class="nowrap">#{{ order.id }}</td>
                    <td>
                      <a
                        href="#"
                        class="booking-link"
                        @click.prevent="viewBookingDetails(order.booking_id)"
                      >
                        #{{ order.booking_id }}
                      </a>
                    </td>
                    <td>
                      <div class="customer-info">
                        <div class="customer-name">{{ order.customer_name }}</div>
                        <div class="customer-phone">{{ order.customer_phone }}</div>
                      </div>
                    </td>
                    <td>
                      <div class="trip-info">
                        <div class="trip-destination" v-if="order.booking_destination">
                          <i class="fas fa-map-marker-alt"></i> {{ order.booking_destination }}
                        </div>
                        <div class="trip-date" v-if="order.booking_date">
                          <i class="fas fa-calendar-alt"></i> {{ formatDate(order.booking_date) }}
                        </div>
                        <span v-if="!order.booking_destination && !order.booking_date" class="text-muted">N/A</span>
                      </div>
                    </td>
                    <td>
                      <span class="text-truncate" :title="order.product_name">
                        {{ order.product_name }}
                      </span>
                    </td>
                    <td class="text-center">{{ order.quantity }}</td>
                    <td class="text-right nowrap price-value">{{ formatCurrency(order.total_price) }}</td>
                    <td class="nowrap">{{ formatDate(order.order_date) }}</td>
                    <td>
                      <span
                        class="status-badge"
                        :class="order.status.toLowerCase()"
                      >
                        {{ translateStatus(order.status) }}
                      </span>
                    </td>
                    <td class="col-actions">
                      <div class="action-buttons">
                        <button
                          @click="openOrderModal(order)"
                          class="action-btn view-btn"
                          title="Xem chi tiết"
                          aria-label="Xem chi tiết đơn hàng"
                        >
                          <i class="fas fa-eye"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>

              <!-- Mobile Card View -->
              <div class="mobile-card-list">
                <div v-for="order in orders" :key="'card-' + order.id" class="mobile-card">
                  <div class="mobile-card-header">
                    <div class="mobile-card-title">
                      <span class="mobile-card-id">#{{ order.id }}</span>
                      {{ order.product_name }}
                    </div>
                    <span class="status-badge" :class="order.status.toLowerCase()">
                      {{ translateStatus(order.status) }}
                    </span>
                  </div>
                  <div class="mobile-card-body">
                    <div class="mobile-card-row">
                      <span class="mobile-card-label">Khách hàng:</span>
                      <span class="mobile-card-value">
                        {{ order.customer_name }}
                        <small>({{ order.customer_phone }})</small>
                      </span>
                    </div>
                    <div class="mobile-card-row">
                      <span class="mobile-card-label">Booking:</span>
                      <span class="mobile-card-value">
                        <a href="#" class="booking-link" @click.prevent="viewBookingDetails(order.booking_id)">
                          #{{ order.booking_id }}
                        </a>
                      </span>
                    </div>
                    <div class="mobile-card-row" v-if="order.booking_destination || order.booking_date">
                      <span class="mobile-card-label">Chuyến đi:</span>
                      <span class="mobile-card-value">
                        <span v-if="order.booking_destination">{{ order.booking_destination }}</span>
                        <br v-if="order.booking_destination && order.booking_date" />
                        <small v-if="order.booking_date">
                          <i class="fas fa-calendar-alt"></i> {{ formatDate(order.booking_date) }}
                        </small>
                      </span>
                    </div>
                    <div class="mobile-card-row">
                      <span class="mobile-card-label">Số lượng:</span>
                      <span class="mobile-card-value">{{ order.quantity }}</span>
                    </div>
                    <div class="mobile-card-row">
                      <span class="mobile-card-label">Tổng tiền:</span>
                      <span class="mobile-card-value price-value">{{ formatCurrency(order.total_price) }}</span>
                    </div>
                    <div class="mobile-card-row">
                      <span class="mobile-card-label">Ngày đặt:</span>
                      <span class="mobile-card-value">{{ formatDate(order.order_date) }}</span>
                    </div>
                  </div>
                  <div class="mobile-card-actions">
                    <button class="action-btn view-btn with-label" @click="openOrderModal(order)">
                      <i class="fas fa-eye"></i> Chi tiết
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Pagination -->
          <div class="pagination-container" v-if="!isLoading && orders.length > 0">
            <div class="pagination-info">
              Hiển thị {{ orders.length }} đơn hàng
            </div>
            <div class="pagination-controls">
              <button
                class="pagination-btn"
                :disabled="currentPage === 1"
                @click="changePage(currentPage - 1)"
              >
                <i class="fas fa-chevron-left"></i> Trước
              </button>
              <span class="page-info">
                Trang {{ currentPage }} / {{ totalPages }}
              </span>
              <button
                class="pagination-btn"
                :disabled="currentPage === totalPages || orders.length === 0"
                @click="changePage(currentPage + 1)"
              >
                Sau <i class="fas fa-chevron-right"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Order Detail Modal -->
    <div class="modal" v-if="selectedOrder" @click.self="closeModal">
      <div class="modal-content">
        <div class="modal-header">
          <h2>Chi tiết đơn hàng phụ trợ #{{ selectedOrder.id }}</h2>
          <button class="close-btn" @click="closeModal">×</button>
        </div>

        <div class="modal-body">
          <!-- Order Summary -->
          <div class="summary-section">
            <div class="order-info-header">
              <div class="order-status">
                <span
                  class="status-badge large"
                  :class="selectedOrder.status.toLowerCase()"
                >
                  {{ translateStatus(selectedOrder.status) }}
                </span>
              </div>
              <div class="order-date">
                <i class="fas fa-calendar-alt"></i>
                Ngày đặt: {{ formatDate(selectedOrder.order_date) }}
              </div>
            </div>
          </div>

          <!-- Customer Information Section -->
          <div class="detail-section">
            <h3><i class="fas fa-user"></i> Thông tin khách hàng</h3>
            <div class="section-content">
              <div class="detail-grid">
                <div class="detail-group">
                  <label>Họ tên</label>
                  <div class="detail-value">
                    {{ selectedOrder.customer_name }}
                  </div>
                </div>
                <div class="detail-group">
                  <label>Số điện thoại</label>
                  <div class="detail-value">
                    {{ selectedOrder.customer_phone }}
                  </div>
                </div>
                <div class="detail-group">
                  <label>Email</label>
                  <div class="detail-value">
                    {{ selectedOrder.customer_email || 'Không có' }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Related Booking Information -->
          <div class="detail-section">
            <h3>
              <i class="fas fa-ticket-alt"></i> Thông tin đặt vé liên quan
            </h3>
            <div class="section-content">
              <div class="booking-info">
                <div class="detail-group">
                  <label>Booking ID</label>
                  <div class="detail-value">
                    <a
                      href="#"
                      @click.prevent="
                        viewBookingDetails(selectedOrder.booking_id)
                      "
                      class="booking-link"
                    >
                      #{{ selectedOrder.booking_id }}
                    </a>
                  </div>
                </div>
                <div
                  class="detail-group"
                  v-if="selectedOrder.booking_destination && selectedBookingDetails"
                >
                  <label>Tuyến đường</label>
                  <div class="detail-value">
                    {{ selectedBookingDetails?.route || 'Không có thông tin' }}
                  </div>
                </div>
                <div class="detail-group" v-if="selectedOrder.booking_date">
                  <label>Ngày khởi hành</label>
                  <div class="detail-value">
                    {{ formatDate(selectedOrder.booking_date) }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Order Items Section -->
          <div class="detail-section">
            <h3><i class="fas fa-shopping-basket"></i> Chi tiết đơn hàng</h3>
            <div class="section-content">
              <div class="items-table">
                <table>
                  <thead>
                    <tr>
                      <th>Sản phẩm</th>
                      <th>Mô tả</th>
                      <th>Số lượng</th>
                      <th>Đơn giá</th>
                      <th>Thành tiền</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{{ selectedOrder.product_name }}</td>
                      <td>
                        {{
                          selectedOrder.product_description || 'Không có mô tả'
                        }}
                      </td>
                      <td>{{ selectedOrder.quantity }}</td>
                      <td>{{ formatCurrency(selectedOrder.price) }}</td>
                      <td>{{ formatCurrency(selectedOrder.total_price) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Total Information -->
              <div class="order-summary">
                <div class="summary-row total">
                  <div class="summary-label">Tổng cộng:</div>
                  <div class="summary-value">
                    {{ formatCurrency(selectedOrder.total_price) }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Order Note Section (if any) -->
          <div class="detail-section" v-if="selectedOrder.notes">
            <h3><i class="fas fa-sticky-note"></i> Ghi chú</h3>
            <div class="section-content">
              <div class="order-notes">
                {{ selectedOrder.notes }}
              </div>
            </div>
          </div>

          <!-- Order Status Update Section -->
          <div class="detail-section">
            <h3><i class="fas fa-cog"></i> Cập nhật trạng thái</h3>
            <div class="section-content">
              <div class="status-update">
                <div class="status-select">
                  <label for="order-status">Trạng thái đơn hàng:</label>
                  <select
                    id="order-status"
                    v-model="selectedOrder.status"
                    class="edit-input"
                  >
                    <option value="pending">Chờ xử lý</option>
                    <option value="confirmed">Đã xác nhận</option>
                    <option value="shipped">Đang giao hàng</option>
                    <option value="completed">Hoàn thành</option>
                    <option value="canceled">Đã hủy</option>
                  </select>
                  <div class="status-help-text">
                    Chọn trạng thái để cập nhật đơn hàng này
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Add Note Section -->
          <div class="detail-section">
            <h3><i class="fas fa-edit"></i> Thêm ghi chú</h3>
            <div class="section-content">
              <div class="note-input">
                <textarea
                  v-model="selectedOrder.notes"
                  rows="3"
                  placeholder="Nhập ghi chú về đơn hàng này..."
                  class="edit-input"
                ></textarea>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button class="cancel-btn" @click="closeModal">Đóng</button>
          <button class="save-btn" @click="updateOrderStatus">
            <i class="fas fa-save"></i> Lưu thay đổi
          </button>
        </div>
      </div>
    </div>

    <div
      class="modal"
      v-if="selectedBookingDetails"
      @click.self="closeBookingModal"
    >
      <div class="modal-content">
        <div class="modal-header">
          <h2>Chi tiết đặt vé #{{ selectedBookingDetails.id }}</h2>
          <button class="close-btn" @click="closeBookingModal">×</button>
        </div>

        <div class="modal-body" v-if="!isLoadingBookingDetails">
          <!-- Booking Status -->
          <div class="summary-section">
            <div class="order-info-header">
              <div class="order-status">
                <span
                  class="status-badge large"
                  :class="selectedBookingDetails.status.toLowerCase()"
                >
                  {{ translateStatus(selectedBookingDetails.status) }}
                </span>
              </div>
              <div class="order-date">
                <i class="fas fa-calendar-alt"></i>
                Ngày đặt: {{ formatDate(selectedBookingDetails.date) }}
              </div>
            </div>
          </div>

          <!-- Customer Information -->
          <div class="detail-section">
            <h3><i class="fas fa-user"></i> Thông tin khách hàng</h3>
            <div class="section-content">
              <div class="detail-grid">
                <div class="detail-group">
                  <label>Họ tên</label>
                  <div class="detail-value">
                    {{ selectedBookingDetails.customerName }}
                  </div>
                </div>
                <div class="detail-group">
                  <label>Số điện thoại</label>
                  <div class="detail-value">
                    {{ selectedBookingDetails.customerPhone }}
                  </div>
                </div>
                <div class="detail-group">
                  <label>Email</label>
                  <div class="detail-value">
                    {{ selectedBookingDetails.customerEmail || 'Không có' }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Trip Information -->
          <div class="detail-section" v-if="selectedBookingDetails">
            <h3><i class="fas fa-route"></i> Thông tin chuyến đi</h3>
            <div class="section-content">
              <div class="detail-grid">
                <div class="detail-group">
                  <label>Tuyến đường</label>
                  <div class="detail-value">
                    {{ selectedBookingDetails.route || 'Không có thông tin' }}
                  </div>
                </div>
                <div class="detail-group">
                  <label>Loại vé</label>
                  <div class="detail-value">
                    {{ selectedBookingDetails.ticketType }}
                  </div>
                </div>
                <div class="detail-group">
                  <label>Giá vé</label>
                  <div class="detail-value">
                    {{ formatCurrency(selectedBookingDetails.ticketPrice) }}
                  </div>
                </div>
                <div class="detail-group">
                  <label>Ghế</label>
                  <div class="detail-value highlight-value">
                    <span class="seat-number">{{
                      selectedBookingDetails.slotNumber
                    }}</span>
                  </div>
                </div>
                <div class="detail-group">
                  <label>Điểm đón</label>
                  <div class="detail-value">
                    {{ selectedBookingDetails.pickupLocation }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Loading state -->
        <div class="modal-body" v-else>
          <div class="loading-state">
            <i class="fas fa-spinner fa-spin"></i>
            <p>Đang tải thông tin đặt vé...</p>
          </div>
        </div>

        <div class="modal-footer">
          <button class="cancel-btn" @click="closeBookingModal">Đóng</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axiosInstance from '@/plugins/axios'
import AdminSidebar from '../common/AdminSidebar.vue'
import { ToastService } from '@/services/ToastService'

const isProd = process.env.VUE_APP_IS_PROD === 'true'
const API_BASE_URL = isProd
  ? process.env.VUE_APP_BACKEND_URL_PROD
  : process.env.VUE_APP_BACKEND_URL_DEV

export default {
  name: 'AdminAddonOrders',
  components: {
    AdminSidebar
  },
  data () {
    return {
      currentUser: {
        full_name: '',
        role: ''
      },
      isSidebarOpen: true,
      orders: [],
      selectedOrder: null,
      searchQuery: '',
      statusFilter: '',
      dateFrom: '',
      dateTo: '',
      currentPage: 1,
      totalPages: 1,
      totalItems: 0,
      perPage: 10,
      isLoading: false,
      selectedBookingDetails: null,
      isLoadingBookingDetails: false
    }
  },
  created () {
    // this.getCurrentUser()
    this.fetchOrders()

    // Retrieve sidebar state from local storage
    const sidebarState = localStorage.getItem('sidebarOpen')
    if (sidebarState !== null) {
      this.isSidebarOpen = sidebarState === 'true'
    }
  },
  methods: {
    // async getCurrentUser () {
    //   try {
    //     const token = localStorage.getItem('adminToken')
    //     const response = await axiosInstance.get(`${API_BASE_URL}/admin/me`, {
    //       headers: { Authorization: `Bearer ${token}` }
    //     })
    //     this.currentUser = response.data
    //   } catch (error) {
    //     console.error('Error fetching user data:', error)
    //     if (error.response?.status === 401) {
    //       this.handleLogout()
    //     }
    //   }
    // },
    async fetchOrders () {
      try {
        this.isLoading = true
        const token = localStorage.getItem('adminToken')

        // Create params object
        const params = {
          page: this.currentPage,
          per_page: this.perPage
        }

        if (this.searchQuery) params.search = this.searchQuery
        if (this.statusFilter) params.status = this.statusFilter

        if (this.dateFrom) {
          const fromDate = new Date(this.dateFrom)
          fromDate.setHours(0, 0, 0, 0)
          params.from_date = fromDate.toISOString()
        }

        if (this.dateTo) {
          const toDate = new Date(this.dateTo)
          toDate.setHours(23, 59, 59, 999)
          params.to_date = toDate.toISOString()
        }

        const response = await axiosInstance.get(
          `${API_BASE_URL}/admin/booking-addons/`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params
          }
        )

        this.orders = response.data.items.map(item => {
          return {
            id: item.booking_addon_id,
            booking_id: item.booking_id,
            addon_id: item.addon_id,
            product_name: item.name || 'Sản phẩm không xác định',
            product_description: item.description || '',
            quantity: item.quantity,
            price: parseFloat(item.price),
            total_price: parseFloat(item.price) * item.quantity,
            order_date: item.created_at,
            status: item.status || 'pending',
            customer_name: item.customer_name || 'Khách hàng',
            customer_phone: item.customer_phone || '',
            customer_email: item.customer_email || '',
            notes: item.notes || '',
            booking_destination: item.booking_destination || '',
            booking_date: item.booking_date || null
          }
        })

        this.totalItems = response.data.total || 0
        this.totalPages = response.data.pages || 1
      } catch (error) {
        console.error('Error fetching orders:', error)
        ToastService.error('Có lỗi khi tải dữ liệu đơn hàng')

        if (error.response?.status === 401) {
          this.handleLogout()
        }
      } finally {
        this.isLoading = false
      }
    },
    closeBookingModal () {
      this.selectedBookingDetails = null
    },
    changePage (newPage) {
      this.currentPage = newPage
      this.fetchOrders()
    },
    resetFilters () {
      this.searchQuery = ''
      this.statusFilter = ''
      this.dateFrom = ''
      this.dateTo = ''
      this.currentPage = 1
      this.fetchOrders()
    },
    openOrderModal (order) {
      this.selectedOrder = JSON.parse(JSON.stringify(order)) // Deep copy
    },
    closeModal () {
      this.selectedOrder = null
    },
    async updateOrderStatus () {
      if (!this.selectedOrder) return

      try {
        const token = localStorage.getItem('adminToken')

        // Use the specific booking-addons endpoint for updating addon orders
        await axiosInstance.put(
          `${API_BASE_URL}/admin/booking-addons/${this.selectedOrder.id}`,
          {
            status: this.selectedOrder.status,
            notes: this.selectedOrder.notes
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        )

        ToastService.success('Cập nhật đơn hàng thành công')
        this.closeModal()
        this.fetchOrders()
      } catch (error) {
        console.error('Error updating order:', error)
        ToastService.error('Có lỗi khi cập nhật đơn hàng')
      }
    },
    formatCurrency (amount) {
      if (!amount && amount !== 0) return 'N/A'
      return new Intl.NumberFormat('vi-VN', {
        style: 'currency',
        currency: 'VND'
      }).format(amount)
    },
    formatDate (date) {
      if (!date) return '-'
      try {
        return new Date(date).toLocaleDateString('vi-VN', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      } catch (error) {
        console.error('Date formatting error:', error)
        return '-'
      }
    },
    translateStatus (status) {
      const statusMap = {
        pending: 'Chờ xử lý',
        confirmed: 'Đã xác nhận',
        shipped: 'Đang giao hàng',
        completed: 'Hoàn thành',
        canceled: 'Đã hủy'
      }

      return statusMap[status.toLowerCase()] || status
    },
    async viewBookingDetails (bookingId) {
      try {
        this.isLoadingBookingDetails = true
        ToastService.info('Đang tải thông tin đặt vé...')

        const token = localStorage.getItem('adminToken')
        const response = await axiosInstance.get(
          `${API_BASE_URL}/admin/bookings/${bookingId}`,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        )

        // Format booking details for display with null checks
        const bookingData = response.data || {}

        this.selectedBookingDetails = {
          id: bookingData.booking_id || bookingId,
          customerName: bookingData.customer_name || 'Không có thông tin',
          customerPhone: bookingData.customer_phone || '',
          customerEmail: bookingData.customer_email || '',
          route: bookingData.route?.destination || 'Không có thông tin',
          date: bookingData.booking_datetime || new Date(),
          status: bookingData.status || 'pending',
          ticketType: bookingData.ticket?.type || 'Không xác định',
          ticketPrice: bookingData.ticket?.price || 0,
          pickupLocation: bookingData.pickup_location?.location_name || 'Không xác định',
          slotNumber: bookingData.slot_number || 'N/A'
        }
      } catch (error) {
        console.error('Error fetching booking details:', error)
        ToastService.error('Không thể tải thông tin đặt vé')

        // Set default values even on error to prevent null reference
        this.selectedBookingDetails = {
          id: bookingId,
          customerName: 'Không tìm thấy thông tin',
          customerPhone: '',
          customerEmail: '',
          route: 'Không có thông tin',
          date: new Date(),
          status: 'pending',
          ticketType: 'Không xác định',
          ticketPrice: 0,
          pickupLocation: 'Không xác định',
          slotNumber: 'N/A'
        }
      } finally {
        this.isLoadingBookingDetails = false
      }
    },
    handleLogout () {
      localStorage.removeItem('adminToken')
      localStorage.removeItem('refreshToken')
      this.$router.push('/login')
    },
    toggleSidebar () {
      this.isSidebarOpen = !this.isSidebarOpen
      localStorage.setItem('sidebarOpen', this.isSidebarOpen.toString())
    }
  }
}
</script>

<style scoped>
@import '@/assets/styles/admin.scss';
@import '@/assets/styles/admin-table.scss';

.trip-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 0.85rem;
}

.trip-destination {
  font-weight: 500;
  color: #1f2937;
}

.trip-destination i {
  color: #ef4444;
  font-size: 0.75rem;
  margin-right: 2px;
}

.trip-date {
  color: #6b7280;
  font-size: 0.8rem;
}

.trip-date i {
  color: #3b82f6;
  font-size: 0.7rem;
  margin-right: 2px;
}

.text-muted {
  color: #9ca3af;
  font-style: italic;
}

.admin-content {
  padding: 24px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
}

.section-header h2 {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1f2937;
  display: flex;
  align-items: center;
  gap: 10px;
}

.search-filters {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
}

.filters-container {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  width: 100%;
}

.search-box {
  position: relative;
  width: 100%;
  max-width: 480px;
}

.search-box input {
  width: 100%;
  padding: 10px 40px 10px 40px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 0.95rem;
  transition: all 0.2s;
  background-color: white;
}

.search-box input:focus {
  border-color: #3b82f6;
  outline: none;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.search-box i {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  font-size: 16px;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.filter-group label {
  font-size: 0.85rem;
  font-weight: 500;
  color: #4b5563;
}

.filter-group select,
.filter-group input {
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 0.9rem;
  background-color: white;
  min-width: 160px;
}

.apply-filter-btn,
.reset-filter-btn {
  align-self: flex-end;
  padding: 8px 16px;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 21px;
}

.apply-filter-btn {
  background-color: #3b82f6;
  color: white;
  border: none;
}

.apply-filter-btn:hover {
  background-color: #2563eb;
}

.reset-filter-btn {
  background-color: #f3f4f6;
  color: #4b5563;
  border: 1px solid #e5e7eb;
}

.reset-filter-btn:hover {
  background-color: #e5e7eb;
}

.table-responsive {
  overflow-x: auto;
  margin-bottom: 24px;
  border-radius: 8px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
  border: 1px solid #e5e7eb;
}

table {
  /* Add striped rows for better readability */
  background-color: white;
}

tbody tr:nth-child(even) {
  background-color: #f9fafb;
}

tbody tr:hover {
  background-color: #f0f9ff; /* Light blue highlight */
}

th {
  position: sticky;
  top: 0;
  background-color: #f3f4f6;
  padding: 14px 20px;
  text-align: left;
  font-weight: 600;
  color: #4b5563;
  border-bottom: 2px solid #e5e7eb;
  z-index: 10; /* Ensure header stays above content when scrolling */
}

/* Add vertical borders for column separation */
td,
th {
  border-right: 1px solid #e5e7eb;
}

td:last-child,
th:last-child {
  border-right: none;
}

/* For columns with wrapping content */
.customer-name,
.product {
  white-space: normal;
}

th {
  background-color: #f9fafb;
  padding: 14px 20px;
  text-align: left;
  font-weight: 600;
  color: #4b5563;
  border-bottom: 1px solid #e5e7eb;
}

td {
  padding: 14px 20px;
  border-bottom: 1px solid #f3f4f6;
  color: #374151;
}

tbody tr:hover {
  background-color: #f9fafb;
}

tbody tr:last-child td {
  border-bottom: none;
}

.order-id {
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
}

a {
  color: #3b82f6;
  text-decoration: none;
}

a:hover {
  text-decoration: underline;
}

.customer-name {
  font-weight: 500;
}

.customer-phone {
  font-size: 0.85rem;
  color: #6b7280;
  margin-top: 2px;
}

.price {
  font-weight: 500;
}

.quantity {
  text-align: center;
  font-weight: 500;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 500;
  width: fit-content;
  min-width: 100px; /* Add a minimum width for consistent sizing */
}

.status-badge.large {
  padding: 6px 16px;
  font-size: 0.95rem;
  min-width: 120px;
}

/* Enhanced colors for better contrast */
.status-badge.pending {
  background-color: #fff7ed;
  color: #9a3412;
  border: 1px solid #fed7aa;
}

.status-badge.confirmed {
  background-color: #eff6ff;
  color: #1e40af;
  border: 1px solid #bfdbfe;
}

.status-badge.shipped {
  background-color: #f5f3ff;
  color: #6d28d9;
  border: 1px solid #ddd6fe;
}

.status-badge.completed {
  background-color: #f0fdf4;
  color: #166534;
  border: 1px solid #bbf7d0;
}

.status-badge.canceled {
  background-color: #fef2f2;
  color: #b91c1c;
  border: 1px solid #fecaca;
}

.action-btn {
  padding: 6px 12px;
  border-radius: 6px;
  border: none;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-width: 90px;
}

.view-btn {
  background-color: #f3f4f6;
  color: #4b5563;
  border: 1px solid #e5e7eb;
}

.view-btn:hover {
  background-color: #e5e7eb;
  color: #374151;
}

/* Modal footer buttons */
.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid #e5e7eb;
  background-color: #f9fafb;
}

.cancel-btn,
.save-btn {
  padding: 10px 20px;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
}

.cancel-btn {
  background-color: white;
  color: #4b5563;
  border: 1px solid #d1d5db;
}

.cancel-btn:hover {
  background-color: #f3f4f6;
}

.save-btn {
  background-color: #3b82f6;
  color: white;
  border: none;
}

.save-btn:hover {
  background-color: #2563eb;
}

.no-data {
  text-align: center;
  padding: 40px 0;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  color: #9ca3af;
}

.empty-state i {
  font-size: 3rem;
  opacity: 0.6;
}

.empty-state p {
  font-size: 1rem;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
}

.pagination-btn {
  padding: 8px 16px;
  background-color: white;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  color: #4b5563;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.pagination-btn:hover:not(:disabled) {
  background-color: #f9fafb;
  border-color: #d1d5db;
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  font-weight: 500;
  color: #4b5563;
}

/* Modal Styles */
.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background-color: #ffffff;
  border-radius: 10px;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
}

.modal-body {
  padding: 20px 24px;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 16px;
  margin-top: 12px;
}

.detail-group {
  display: flex;
  flex-direction: column;
  min-width: 0; /* Fix for overflow issues */
}

.detail-value {
  padding: 8px 12px;
  background-color: #f9fafb;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  margin-top: 6px;
  min-height: 38px;
  display: flex;
  align-items: center;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Better section spacing */
.detail-section {
  margin-bottom: 24px;
  border-radius: 8px;
  background-color: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.detail-section h3 {
  padding: 14px 20px;
  background-color: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  margin: 0;
  font-size: 1rem;
  display: flex;
  align-items: center;
  gap: 8px;
}

.section-content {
  padding: 16px 20px;
}
</style>
